import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useSuperAdminFetch, useSuperAdmin, Account, ImpersonationSession } from "@/contexts/SuperAdminContext";
import AccountForm from "./AccountForm";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Plus,
  Building2,
  Users,
  ShieldOff,
  Pencil,
  CheckCircle,
  Ban,
  AlertCircle,
  UserCog,
  Loader2,
  Trash2,
  CheckCircle2,
  ScrollText,
  Eye,
  ChevronLeft,
  ChevronRight,
  Search,
  Database,
  AlertTriangle,
} from "lucide-react";

function StatusBadge({ status }: { status: Account["status"] }) {
  if (status === "active") {
    return (
      <Badge className="bg-emerald-500/15 text-emerald-400 border-emerald-500/20 hover:bg-emerald-500/20">
        Active
      </Badge>
    );
  }
  if (status === "suspended") {
    return (
      <Badge className="bg-red-500/15 text-red-400 border-red-500/20 hover:bg-red-500/20">
        Suspended
      </Badge>
    );
  }
  return (
    <Badge className="bg-amber-500/15 text-amber-400 border-amber-500/20 hover:bg-amber-500/20">
      Pending
    </Badge>
  );
}

interface TenantUser {
  id: number;
  email: string;
  name: string;
  main_role: string;
  sub_role: string | null;
  avatar_color: string;
  initials: string;
  employee_id: number | null;
  avatar_url: string | null;
  department: string | null;
  job_title: string | null;
}

export default function AccountsList() {
  const apiFetch = useSuperAdminFetch();
  const navigate = useNavigate();
  const { startImpersonation } = useSuperAdmin();
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [formOpen, setFormOpen] = useState(false);
  const [editingAccount, setEditingAccount] = useState<Account | undefined>();

  // Create Admin dialog
  const [adminDialogAccount, setAdminDialogAccount] = useState<Account | null>(null);
  const [adminName, setAdminName] = useState("");
  const [adminEmail, setAdminEmail] = useState("");
  const [adminPassword, setAdminPassword] = useState("");
  const [adminSaving, setAdminSaving] = useState(false);
  const [adminError, setAdminError] = useState("");
  const [adminSuccess, setAdminSuccess] = useState("");

  // Impersonate dialog
  const [impersonateAccount, setImpersonateAccount] = useState<Account | null>(null);
  const [tenantUsers, setTenantUsers] = useState<TenantUser[]>([]);
  const [tenantUsersLoading, setTenantUsersLoading] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
  const [userSearch, setUserSearch] = useState("");
  const [impersonating, setImpersonating] = useState(false);
  const [impersonateError, setImpersonateError] = useState("");

  // Load Dummy Data dialog + in-flight tracking
  const [dummyDataAccount, setDummyDataAccount] = useState<Account | null>(null);
  const [dummyDataLoadingId, setDummyDataLoadingId] = useState<number | null>(null);

  const openAuditLog = (acc: Account) => {
    navigate(`/superadmin/accounts/${acc.id}/audit-log`);
  };

  // Load tenant users when the impersonate dialog opens
  useEffect(() => {
    if (!impersonateAccount) return;
    setTenantUsersLoading(true);
    setSelectedUserId(null);
    setUserSearch("");
    setTenantUsers([]);
    apiFetch<TenantUser[]>(`/api/superadmin/accounts/${impersonateAccount.id}/tenant-users`)
      .then(setTenantUsers)
      .catch(() => setTenantUsers([]))
      .finally(() => setTenantUsersLoading(false));
  }, [impersonateAccount, apiFetch]);

  const handleImpersonate = async () => {
    if (!impersonateAccount || !selectedUserId) return;
    setImpersonating(true);
    setImpersonateError("");
    try {
      const data = await apiFetch<{
        token: string; sessionId: number; expiresAt: string; role: string;
        tenantSlug: string; userId: number; userName: string;
        appJwt?: string; appUser?: Record<string, any>;
      }>(`/api/superadmin/accounts/${impersonateAccount.id}/impersonate`, {
        method: "POST",
        body: JSON.stringify({ userId: selectedUserId }),
      });
      const session: ImpersonationSession = {
        token: data.token,
        sessionId: data.sessionId,
        accountId: impersonateAccount.id,
        role: data.role,
        tenantSlug: data.tenantSlug,
        tenantName: impersonateAccount.name,
        expiresAt: data.expiresAt,
        userName: data.userName,
        appJwt: data.appJwt,
        appUser: data.appUser,
      };
      startImpersonation(session);
      setImpersonateAccount(null);
      // Full reload so AuthContext re-initialises from localStorage with the
      // injected app JWT + user object — navigate() alone won't re-run useState.
      window.location.replace("/");
    } catch (err: any) {
      setImpersonateError(err.message || "Failed to start impersonation");
    } finally {
      setImpersonating(false);
    }
  };

  const fetchAccounts = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const data = await apiFetch<Account[]>("/api/superadmin/accounts");
      setAccounts(data);
    } catch (err: any) {
      setError(err.message || "Failed to load accounts");
    } finally {
      setLoading(false);
    }
  }, [apiFetch]);

  useEffect(() => {
    fetchAccounts();
  }, [fetchAccounts]);

  const handleSuspend = async (id: number) => {
    if (!confirm("Are you sure you want to suspend this account? They will lose access immediately.")) return;
    try {
      await apiFetch(`/api/superadmin/accounts/${id}`, { method: "DELETE" });
      fetchAccounts();
    } catch (err: any) {
      alert(err.message || "Failed to suspend account");
    }
  };

  const handleActivate = async (id: number) => {
    try {
      await apiFetch(`/api/superadmin/accounts/${id}`, {
        method: "PATCH",
        body: JSON.stringify({ status: "active" }),
      });
      fetchAccounts();
    } catch (err: any) {
      alert(err.message || "Failed to activate account");
    }
  };

  const handleDestroy = async (acc: Account) => {
    if (!confirm(`PERMANENTLY DELETE "${acc.name}" (${acc.slug})?\n\nThis will drop the entire database schema and all data. This action CANNOT be undone.`)) return;
    if (!confirm(`Are you absolutely sure? Type the account slug to confirm.\n\nThis will delete: ${acc.slug}`)) return;
    try {
      await apiFetch(`/api/superadmin/accounts/${acc.id}/destroy`, { method: "POST" });
      fetchAccounts();
    } catch (err: any) {
      alert(err.message || "Failed to delete account");
    }
  };

  const handleLoadDummyData = async () => {
    if (!dummyDataAccount || dummyDataLoadingId) return;
    const target = dummyDataAccount;
    setDummyDataLoadingId(target.id);
    setDummyDataAccount(null);

    const startToast = toast.loading(`Loading demo data for ${target.name}…`, {
      description:
        "Running tenant migrations and seeding employees, departments, leave, recruitment, payroll, etc. " +
        "This usually takes 30-60 seconds.",
    });

    let finalStatus: string | null = null;
    let finalError: string | null = null;
    let finalSummary: any = null;

    // Synchronous primary path: the route awaits the seed and returns the
    // final status. On Vercel the request can take up to 5 minutes (the
    // maxDuration cap) — the browser keeps the connection open until then.
    try {
      const result = await apiFetch<{
        ok: boolean;
        status: "completed" | "completed_with_warnings" | "failed" | "started";
        error?: string | null;
        summary?: any;
        message?: string;
      }>(`/api/superadmin/accounts/${target.id}/load-dummy-data`, {
        method: "POST",
        body: JSON.stringify({}),
      });
      finalStatus = result.status;
      finalError = result.error ?? null;
      finalSummary = result.summary;
    } catch (err: any) {
      console.warn("Initial seed request failed, falling back to polling:", err);
    }

    // Polling fallback: only if the request errored out before returning a
    // definitive status (network drop, gateway timeout). The seed runs
    // server-side independent of the client connection, so polling will
    // pick up the result once it's recorded in master.accounts.
    if (!finalStatus || finalStatus === "started") {
      const startedAt = Date.now();
      const POLL_INTERVAL_MS = 3000;
      const POLL_TIMEOUT_MS = 5 * 60_000;
      while (Date.now() - startedAt < POLL_TIMEOUT_MS) {
        await new Promise((r) => setTimeout(r, POLL_INTERVAL_MS));
        try {
          const status = await apiFetch<{
            account: { dummy_data_status: string; dummy_data_error: string | null };
          }>(`/api/superadmin/accounts/${target.id}/dummy-data-status`);
          const s = status.account.dummy_data_status;
          if (s === "completed" || s === "completed_with_warnings" || s === "failed") {
            finalStatus = s;
            finalError = status.account.dummy_data_error;
            break;
          }
        } catch { /* keep polling */ }
      }
    }

    toast.dismiss(startToast);

    if (finalStatus === "completed") {
      toast.success("Demo data loaded", {
        description: finalSummary
          ? `${finalSummary.employees ?? 40} employees, ${finalSummary.departments ?? 10} departments, ${finalSummary.recruitmentPositions ?? 5} positions, ${finalSummary.surveys ?? 5} surveys.`
          : `${target.name} has been populated with demo data.`,
      });
    } else if (finalStatus === "completed_with_warnings") {
      toast.warning("Demo data loaded with warnings", {
        description: finalError ?? "Some seed steps failed — check audit logs.",
        duration: 15000,
      });
    } else if (finalStatus === "failed") {
      toast.error("Demo data load failed", {
        description: finalError ?? "Check server logs for details.",
        duration: 15000,
      });
    } else {
      toast.info("Demo data loading is still in progress", {
        description: "Refresh in a minute to see the result.",
        duration: 10000,
      });
    }

    setDummyDataLoadingId(null);
    fetchAccounts();
  };

  const openCreate = () => {
    setEditingAccount(undefined);
    setFormOpen(true);
  };

  const openEdit = (acc: Account) => {
    setEditingAccount(acc);
    setFormOpen(true);
  };

  const openAdminDialog = (acc: Account) => {
    setAdminDialogAccount(acc);
    setAdminName("");
    setAdminEmail(acc.contact_email || "");
    setAdminPassword("");
    setAdminError("");
    setAdminSuccess("");
  };

  const handleCreateAdmin = async () => {
    if (!adminDialogAccount) return;
    setAdminError("");
    setAdminSuccess("");
    if (!adminName.trim() || !adminEmail.trim() || !adminPassword.trim()) {
      setAdminError("All fields are required.");
      return;
    }
    setAdminSaving(true);
    try {
      await apiFetch(`/api/superadmin/accounts/${adminDialogAccount.id}/admin`, {
        method: "POST",
        body: JSON.stringify({ name: adminName.trim(), email: adminEmail.trim(), password: adminPassword }),
      });
      setAdminSuccess("Admin account created successfully. They can now log in.");
      setAdminPassword("");
    } catch (err: any) {
      setAdminError(err.message || "Failed to create admin.");
    } finally {
      setAdminSaving(false);
    }
  };

  const totalAccounts = accounts.length;
  const activeAccounts = accounts.filter(a => a.status === "active").length;
  const suspendedAccounts = accounts.filter(a => a.status === "suspended").length;

  return (
    <div className="p-10 max-w-[1200px] mx-auto">
      {/* Page header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-white">Company Accounts</h1>
          <p className="text-sm text-white/45 mt-1">Manage tenants and their database schemas</p>
        </div>
        <div className="flex items-center gap-3">
          <Button
            onClick={openCreate}
            className="bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500 text-white font-semibold shadow-lg shadow-purple-700/30 gap-2"
          >
            <Plus className="h-4 w-4" />
            New Account
          </Button>
        </div>
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-3 gap-4 mb-8">
        <Card className="bg-white/[0.03] border-white/[0.08]">
          <CardHeader className="pb-2 pt-5 px-5">
            <CardTitle className="text-xs font-semibold text-white/40 uppercase tracking-wider">
              Total Accounts
            </CardTitle>
          </CardHeader>
          <CardContent className="pb-5 px-5 pt-0">
            <div className="flex items-end gap-2">
              <span className="text-3xl font-bold text-white">{loading ? "—" : totalAccounts}</span>
              <Building2 className="h-5 w-5 text-white/20 mb-1" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white/[0.03] border-white/[0.08]">
          <CardHeader className="pb-2 pt-5 px-5">
            <CardTitle className="text-xs font-semibold text-white/40 uppercase tracking-wider">
              Active
            </CardTitle>
          </CardHeader>
          <CardContent className="pb-5 px-5 pt-0">
            <div className="flex items-end gap-2">
              <span className="text-3xl font-bold text-emerald-400">{loading ? "—" : activeAccounts}</span>
              <Users className="h-5 w-5 text-emerald-500/30 mb-1" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white/[0.03] border-white/[0.08]">
          <CardHeader className="pb-2 pt-5 px-5">
            <CardTitle className="text-xs font-semibold text-white/40 uppercase tracking-wider">
              Suspended
            </CardTitle>
          </CardHeader>
          <CardContent className="pb-5 px-5 pt-0">
            <div className="flex items-end gap-2">
              <span className="text-3xl font-bold text-red-400">{loading ? "—" : suspendedAccounts}</span>
              <ShieldOff className="h-5 w-5 text-red-500/30 mb-1" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Error state */}
      {error && (
        <div className="flex items-center gap-2.5 px-4 py-3 rounded-lg bg-red-500/10 border border-red-500/20 text-red-300 text-sm mb-6">
          <AlertCircle className="h-4 w-4 flex-shrink-0" />
          {error}
        </div>
      )}

      {/* Loading skeleton */}
      {!error && loading && (
        <div className="rounded-xl border border-white/[0.08] overflow-hidden bg-white/[0.02]">
          <div className="p-4 border-b border-white/[0.06]">
            <Skeleton className="h-4 w-48 bg-white/[0.06]" />
          </div>
          {[...Array(5)].map((_, i) => (
            <div key={i} className="flex items-center gap-4 px-4 py-4 border-b border-white/[0.04] last:border-0">
              <Skeleton className="h-9 w-9 rounded-lg bg-white/[0.06]" />
              <div className="flex-1 space-y-1.5">
                <Skeleton className="h-3.5 w-40 bg-white/[0.06]" />
                <Skeleton className="h-3 w-28 bg-white/[0.04]" />
              </div>
              <Skeleton className="h-5 w-16 rounded-full bg-white/[0.06]" />
              <Skeleton className="h-3.5 w-24 bg-white/[0.06]" />
              <Skeleton className="h-7 w-16 rounded bg-white/[0.06]" />
            </div>
          ))}
        </div>
      )}

      {/* Empty state */}
      {!error && !loading && accounts.length === 0 && (
        <div className="rounded-xl border border-dashed border-white/10 bg-white/[0.02] py-16 flex flex-col items-center gap-4">
          <div className="w-12 h-12 rounded-full bg-white/5 flex items-center justify-center">
            <Building2 className="h-6 w-6 text-white/30" />
          </div>
          <div className="text-center">
            <h3 className="text-[15px] font-semibold text-[#e6edf3]">No accounts yet</h3>
            <p className="text-sm text-white/40 mt-1.5">Create the first tenant account to get started.</p>
          </div>
          <Button
            onClick={openCreate}
            variant="outline"
            className="border-white/15 text-white/70 hover:text-white hover:bg-white/5 gap-2 mt-1"
          >
            <Plus className="h-4 w-4" />
            Create Account
          </Button>
        </div>
      )}

      {/* Accounts table */}
      {!error && !loading && accounts.length > 0 && (
        <div className="rounded-xl border border-white/[0.08] overflow-hidden bg-white/[0.02]">
          <Table>
            <TableHeader>
              <TableRow className="border-white/[0.08] bg-white/[0.02] hover:bg-white/[0.02]">
                <TableHead className="text-xs font-semibold text-white/40 uppercase tracking-wider py-4">
                  Company
                </TableHead>
                <TableHead className="text-xs font-semibold text-white/40 uppercase tracking-wider py-4">
                  Slug / Schema
                </TableHead>
                <TableHead className="text-xs font-semibold text-white/40 uppercase tracking-wider py-4">
                  Status
                </TableHead>
                <TableHead className="text-xs font-semibold text-white/40 uppercase tracking-wider py-4">
                  Max Users
                </TableHead>
                <TableHead className="text-xs font-semibold text-white/40 uppercase tracking-wider py-4">
                  Created
                </TableHead>
                <TableHead className="py-4 w-[200px]" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {accounts.map(acc => (
                <TableRow
                  key={acc.id}
                  className="border-white/[0.04] hover:bg-white/[0.02]"
                >
                  <TableCell className="py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-lg bg-white/5 flex items-center justify-center text-sm font-semibold text-white flex-shrink-0">
                        {acc.name.charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <div className="font-semibold text-[#e6edf3] text-sm">{acc.name}</div>
                        {acc.contact_email && (
                          <div className="text-xs text-white/40 mt-0.5">{acc.contact_email}</div>
                        )}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="py-4">
                    <div className="text-sm font-medium text-[#e6edf3]">{acc.slug}.portal.besthr.app</div>
                    <div className="text-xs text-white/30 mt-0.5 font-mono">schema: {acc.schema_name}</div>
                  </TableCell>
                  <TableCell className="py-4">
                    <StatusBadge status={acc.status} />
                  </TableCell>
                  <TableCell className="py-4 text-sm text-white/60">
                    {acc.max_users ?? <span className="text-white/25">Unlimited</span>}
                  </TableCell>
                  <TableCell className="py-4 text-sm text-white/55">
                    {new Date(acc.created_at).toLocaleDateString()}
                  </TableCell>
                  <TableCell className="py-3">
                    <div className="flex flex-col gap-1 items-end">
                      {/* Row 1 — primary actions */}
                      <div className="flex items-center gap-1">
                        {acc.status === "active" && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => { setImpersonateAccount(acc); setImpersonateError(""); }}
                            className="h-7 px-2.5 text-xs text-amber-400/80 hover:text-amber-300 hover:bg-amber-500/10 gap-1"
                          >
                            <Eye className="h-3 w-3" />
                            Preview
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => openAdminDialog(acc)}
                          className="h-7 px-2.5 text-xs text-indigo-400/80 hover:text-indigo-300 hover:bg-indigo-500/10 gap-1"
                        >
                          <UserCog className="h-3 w-3" />
                          Admin
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => openEdit(acc)}
                          className="h-7 px-2.5 text-xs text-white/60 hover:text-white hover:bg-white/8 gap-1"
                        >
                          <Pencil className="h-3 w-3" />
                          Edit
                        </Button>
                      </div>
                      {/* Row 2 — secondary / destructive actions */}
                      <div className="flex items-center gap-1">
                        {acc.status === "active" && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setDummyDataAccount(acc)}
                            disabled={dummyDataLoadingId !== null}
                            className="h-7 px-2.5 text-xs text-emerald-400/70 hover:text-emerald-300 hover:bg-emerald-500/10 gap-1"
                          >
                            {dummyDataLoadingId === acc.id ? (
                              <Loader2 className="h-3 w-3 animate-spin" />
                            ) : (
                              <Database className="h-3 w-3" />
                            )}
                            Load Dummy Data
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => openAuditLog(acc)}
                          className="h-7 px-2.5 text-xs text-blue-400/70 hover:text-blue-300 hover:bg-blue-500/10 gap-1"
                        >
                          <ScrollText className="h-3 w-3" />
                          Logs
                        </Button>
                        {acc.status === "active" ? (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleSuspend(acc.id)}
                            className="h-7 px-2.5 text-xs text-orange-400/70 hover:text-orange-400 hover:bg-orange-500/10 gap-1"
                          >
                            <Ban className="h-3 w-3" />
                            Suspend
                          </Button>
                        ) : (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleActivate(acc.id)}
                            className="h-7 px-2.5 text-xs text-emerald-400/70 hover:text-emerald-400 hover:bg-emerald-500/10 gap-1"
                          >
                            <CheckCircle className="h-3 w-3" />
                            Activate
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleDestroy(acc)}
                          className="h-7 px-2.5 text-xs text-red-400/50 hover:text-red-400 hover:bg-red-500/10 gap-1"
                        >
                          <Trash2 className="h-3 w-3" />
                          Delete
                        </Button>
                      </div>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Impersonate Dialog — user picker */}
      <Dialog open={!!impersonateAccount} onOpenChange={open => { if (!open) setImpersonateAccount(null); }}>
        <DialogContent className="bg-[#161b22] border border-white/10 text-white max-w-[520px]">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Eye className="h-5 w-5 text-amber-400" />
              View Tenant as User — {impersonateAccount?.name}
            </DialogTitle>
            <DialogDescription className="text-white/45 text-sm">
              Select a real user to view the portal exactly as they see it.
              The session lasts 30 minutes and is read-only.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 mt-1">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-white/30" />
              <Input
                placeholder="Search by name or email…"
                value={userSearch}
                onChange={e => setUserSearch(e.target.value)}
                className="pl-8 h-8 bg-white/5 border-white/10 text-white placeholder:text-white/25 text-sm focus:border-amber-500/50"
              />
            </div>

            {/* User list */}
            <div className="border border-white/[0.08] rounded-lg overflow-hidden">
              {tenantUsersLoading ? (
                <div className="flex items-center justify-center h-28">
                  <Loader2 className="h-5 w-5 animate-spin text-white/30" />
                </div>
              ) : tenantUsers.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-28 text-white/30 gap-1.5">
                  <Users className="h-6 w-6" />
                  <p className="text-xs">No users found in this tenant</p>
                </div>
              ) : (
                <div className="max-h-60 overflow-y-auto divide-y divide-white/[0.05]">
                  {tenantUsers
                    .filter(u => {
                      const q = userSearch.toLowerCase();
                      return !q || (u.name || "").toLowerCase().includes(q) || u.email.toLowerCase().includes(q);
                    })
                    .map(u => {
                      const roleLabel =
                        u.sub_role && u.sub_role !== "employee"
                          ? ({ line_manager: "Line Manager", c_level: "C-Level" } as Record<string,string>)[u.sub_role] ?? u.sub_role
                          : ({ admin: "Admin", hr: "HR", user: "Employee" } as Record<string,string>)[u.main_role] ?? u.main_role;
                      const roleBadgeClass =
                        u.main_role === "admin" ? "bg-red-500/15 text-red-400 border-red-500/20" :
                        u.main_role === "hr"    ? "bg-purple-500/15 text-purple-400 border-purple-500/20" :
                        u.sub_role === "line_manager" ? "bg-emerald-500/15 text-emerald-400 border-emerald-500/20" :
                        u.sub_role === "c_level"      ? "bg-violet-500/15 text-violet-400 border-violet-500/20" :
                        "bg-blue-500/15 text-blue-400 border-blue-500/20";
                      const isSelected = selectedUserId === u.id;
                      return (
                        <button
                          key={u.id}
                          type="button"
                          onClick={() => setSelectedUserId(u.id)}
                          className={`w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors ${
                            isSelected
                              ? "bg-amber-500/15 border-l-2 border-amber-400"
                              : "hover:bg-white/[0.03] border-l-2 border-transparent"
                          }`}
                        >
                          {/* Avatar */}
                          <div
                            className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold text-white flex-shrink-0"
                            style={{ backgroundColor: u.avatar_color || "#6366f1" }}
                          >
                            {u.initials || (u.name || u.email).slice(0, 2).toUpperCase()}
                          </div>
                          {/* Info */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-medium text-[#e6edf3] truncate">{u.name || u.email}</span>
                              <span className={`inline-flex items-center px-1.5 py-0 rounded text-[10px] font-semibold border ${roleBadgeClass} flex-shrink-0`}>
                                {roleLabel}
                              </span>
                            </div>
                            <div className="text-xs text-white/35 truncate mt-0.5">
                              {u.email}{u.department ? ` · ${u.department}` : ""}
                            </div>
                          </div>
                          {isSelected && (
                            <CheckCircle2 className="h-4 w-4 text-amber-400 flex-shrink-0" />
                          )}
                        </button>
                      );
                    })}
                </div>
              )}
            </div>

            {/* Read-only notice */}
            <div className="flex items-start gap-2 text-amber-400/80 text-xs bg-amber-500/[0.08] border border-amber-500/20 rounded-lg px-3 py-2">
              <AlertCircle className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" />
              <span>Read-only · 30 min · Session is logged in the superadmin audit trail</span>
            </div>

            {impersonateError && (
              <div className="flex items-center gap-2 text-red-400 text-sm bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2.5">
                <AlertCircle className="h-4 w-4 flex-shrink-0" />
                {impersonateError}
              </div>
            )}

            <div className="flex gap-3 pt-1">
              <Button
                variant="ghost"
                className="flex-1 border border-white/10 text-white/60 hover:text-white hover:bg-white/5"
                onClick={() => setImpersonateAccount(null)}
              >
                Cancel
              </Button>
              <Button
                className="flex-1 bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-500 hover:to-orange-500 text-white font-semibold gap-2 disabled:opacity-40"
                onClick={handleImpersonate}
                disabled={impersonating || !selectedUserId}
              >
                {impersonating && <Loader2 className="h-4 w-4 animate-spin" />}
                {impersonating ? "Starting…" : "Start Session →"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Account Form Dialog */}
      <Dialog open={formOpen} onOpenChange={open => { if (!open) setFormOpen(false); }}>
        <DialogContent className="p-0 border-0 bg-transparent shadow-none max-w-[960px] [&>button:last-child]:hidden" aria-describedby={undefined}>
          <DialogTitle className="sr-only">
            {editingAccount ? "Edit Account" : "Create New Account"}
          </DialogTitle>
          <AccountForm
            key={editingAccount?.id ?? "new"}
            account={editingAccount}
            open={formOpen}
            onClose={() => setFormOpen(false)}
            onSaved={() => {
              setFormOpen(false);
              fetchAccounts();
            }}
          />
        </DialogContent>
      </Dialog>

      {/* Create / Reset Admin Dialog */}
      <Dialog open={!!adminDialogAccount} onOpenChange={open => { if (!open) setAdminDialogAccount(null); }}>
        <DialogContent className="bg-[#161b22] border border-white/10 text-white max-w-[440px]">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <UserCog className="h-5 w-5 text-indigo-400" />
              Create / Reset Admin Account
            </DialogTitle>
            <DialogDescription className="text-white/45 text-sm">
              Creates a local admin user in <span className="font-mono text-white/60">{adminDialogAccount?.slug}</span>. If the email already exists, the password and role will be updated.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 mt-2">
            <div className="space-y-1.5">
              <Label className="text-white/60 text-xs">Full Name</Label>
              <Input
                value={adminName}
                onChange={e => setAdminName(e.target.value)}
                placeholder="e.g. Jane Smith"
                className="bg-white/5 border-white/10 text-white placeholder:text-white/25 focus:border-indigo-500/50"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-white/60 text-xs">Email</Label>
              <Input
                type="email"
                value={adminEmail}
                onChange={e => setAdminEmail(e.target.value)}
                placeholder="admin@company.com"
                className="bg-white/5 border-white/10 text-white placeholder:text-white/25 focus:border-indigo-500/50"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-white/60 text-xs">Password</Label>
              <Input
                type="password"
                value={adminPassword}
                onChange={e => setAdminPassword(e.target.value)}
                placeholder="Min. 8 characters"
                className="bg-white/5 border-white/10 text-white placeholder:text-white/25 focus:border-indigo-500/50"
              />
            </div>

            {adminError && (
              <div className="flex items-center gap-2 text-red-400 text-sm bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2.5">
                <AlertCircle className="h-4 w-4 flex-shrink-0" />
                {adminError}
              </div>
            )}
            {adminSuccess && (
              <div className="flex items-center gap-2 text-emerald-400 text-sm bg-emerald-500/10 border border-emerald-500/20 rounded-lg px-3 py-2.5">
                <CheckCircle className="h-4 w-4 flex-shrink-0" />
                {adminSuccess}
              </div>
            )}

            <div className="flex gap-3 pt-1">
              <Button
                variant="ghost"
                className="flex-1 border border-white/10 text-white/60 hover:text-white hover:bg-white/5"
                onClick={() => setAdminDialogAccount(null)}
              >
                Cancel
              </Button>
              <Button
                className="flex-1 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-500 hover:to-purple-500 text-white font-semibold gap-2"
                onClick={handleCreateAdmin}
                disabled={adminSaving}
              >
                {adminSaving && <Loader2 className="h-4 w-4 animate-spin" />}
                {adminSaving ? "Saving…" : "Create Admin"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Load Dummy Data confirmation */}
      <Dialog
        open={!!dummyDataAccount}
        onOpenChange={(open) => {
          if (!open && dummyDataLoadingId === null) setDummyDataAccount(null);
        }}
      >
        <DialogContent className="bg-zinc-950 border-zinc-800 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Database className="h-5 w-5 text-emerald-400" />
              Load Demo Data
            </DialogTitle>
            <DialogDescription className="text-zinc-400">
              This populates the tenant with realistic demo records for every module — perfect for sales demos and screenshots.
            </DialogDescription>
          </DialogHeader>

          <div className="bg-zinc-900/60 border border-zinc-800 rounded-md p-3 text-sm space-y-1">
            <div>
              <span className="text-zinc-500">Tenant:</span>{" "}
              <span className="font-semibold text-emerald-200">{dummyDataAccount?.name}</span>
            </div>
            <div className="text-xs text-zinc-500">
              Will create / refresh: 40 employees · 10 departments · 5 teams · 5 leave requests · KPIs / goals / reviews · expenses + cash advances · 5 concerns · 5 open positions with applicants · 5 payroll runs · documents + templates · onboarding templates + sessions · 5 surveys.
            </div>
          </div>

          <div className="bg-amber-500/5 border border-amber-500/20 rounded-md p-3 text-xs text-amber-200/80 flex gap-2">
            <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
            <div>
              Re-running on the same tenant <strong>refreshes</strong> demo records (idempotent upserts) — it doesn't duplicate them. Existing real data is left untouched. Takes 30-60 seconds.
            </div>
          </div>

          <div className="flex items-center justify-end gap-2 pt-2">
            <Button
              variant="ghost"
              disabled={dummyDataLoadingId !== null}
              onClick={() => setDummyDataAccount(null)}
              className="text-zinc-400 hover:text-white"
            >
              Cancel
            </Button>
            <Button
              onClick={handleLoadDummyData}
              disabled={dummyDataLoadingId !== null}
              className="bg-emerald-600 hover:bg-emerald-500 text-white gap-2"
            >
              {dummyDataLoadingId !== null && <Loader2 className="h-4 w-4 animate-spin" />}
              {dummyDataLoadingId !== null ? "Loading…" : "Continue"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
